import { Map } from 'immutable';
import { memoize, getDisplayName } from '@artsman/ams-loader-utils';


function _zeroLevelPivot() {
  console.log("DEBUG: Building zero level pivot.");
  return this.map(item => item.uniqueKey);
}


function _oneLevelPivot(indexAttribute) {
  console.log("DEBUG: Building one level pivot.", indexAttribute);
  return this.map(item => item.uniqueKey);
}


function _twoLevelPivot(indexAttribute, columnsAttribute) {
  console.log("DEBUG: Building two level pivot.", indexAttribute, columnsAttribute);
   return this.map(item => item.uniqueKey);
}


// Map between the functions used to do the pivot and the names attached to the class when decorated.
// Having this separates the logic of pivoting from the monstrosity that is a JS class decorator.
const _pivotableMap = Map({
  zeroLevelPivot: _zeroLevelPivot,
  oneLevelPivot: _oneLevelPivot,
  twoLevelPivot: _twoLevelPivot
});

/*
 * Decorator to add pivoting functions on a collection.
 *
 * Usage:
 *
 *   @pivotable
 *   class MyCollection extends ModelCollectionBase {}
 *
 *   // Use data loader to get data via React props and @connect
 *   const ApiThings = api.connect({
 *     things: { dataSource: 'things', withMeta: true },
 *   })(Things);
 *
 *   // Use methods on the collection pulled in from the data loader
 *   class Things extends Component {
 *     render() {
 *       let { things } = this.props;
 *
 *       let pivot0Data = things.zeroLevelPivot();
 *       let pivot1Data = things.oneLevelPivot('country');
 *       let pivot2Data = things.twoLevelPivot('country', 'year');
 *
 *       return <div>{ pivot0Data.toJS() }<br/><br/>{ pivot1Data.toJS() }<br/><br/>{ pivot2Data.toJS() }</div>;
 *     }
 *   }
 */
export function pivotable(target) {
  // Extend the target (rather than mutating it)
  let PivotableCollection = class extends target { };

  /*
   * This was intended to be a development mode only check, but introduces a dependency on node/webpack and makes unit
   * testing harder, so we'll just run it in all modes for now. If necessary, it can be optimized later in production mode.
   */
  for (let attribute of _pivotableMap) {
    if (target.prototype.hasOwnProperty(attribute)) {
      throw new TypeError(`Conflict on schema for attribute "${attribute}" on "${target.name}" when adding @pivotable`);
    }
  }

  // Add all pivot methods in the map
  for (let [methodName, fn] of _pivotableMap) {
    // Setup the memoization on the descriptor (only want to pivot once per Collection instance)
    let memoizedDescriptor = memoize(PivotableCollection.prototype, methodName, { value: fn });

    // Add the new memoized descriptor to the decorated class
    Object.defineProperty(PivotableCollection.prototype, methodName, memoizedDescriptor);
  }

  PivotableCollection.displayName = ` Pivotable(${getDisplayName(target)})`;
  PivotableCollection.WrappedSchema = target;

  return PivotableCollection;
}